'use client';

import { useEffect, useRef, useState } from 'react';
import Dialog from '@/components/ui/Dialog';
import FileUploader, { FileInfo } from '@/components/common/upload/FileUploader';
import { useAuthMe } from '@/features/user';
import { useOptions, useSubmitFeedback } from '@/lib/api';
import Captcha, { CaptchaHandle } from '@/components/ui/Captcha';
import InputString from '@/components/ui/InputString';
import { toast } from 'react-toastify';
import { useForm } from 'react-hook-form';
import type { FeedbackFormValues } from '@/types/feedback';

const getInputClassName = (error: boolean) =>
  `w-full p-3 border rounded-lg text-sm ${
    error
      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
      : 'border-gray-300 focus:ring-primary-500 focus:border-primary'
  }`;

interface FeedbackDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function FeedbackDialog({
  isOpen,
  onClose,
  onSuccess,
}: FeedbackDialogProps) {
  const { data: user } = useAuthMe();
  const {
    register,
    handleSubmit,
    formState: { errors },
    clearErrors,
    reset,
    setValue,
    watch,
  } = useForm<FeedbackFormValues>({
    defaultValues: {
      phone: '',
      catId: '',
      feedback: '',
      captcha: '',
    },
  });

  const [fileInfo, setFileInfo] = useState<FileInfo[]>([]);
  const { mutateAsync: createFeedback, isPending: isSubmitting } =
    useSubmitFeedback();
  const captchaRef = useRef<CaptchaHandle>(null);
  const [captchaInput, setCaptchaInput] = useState('');
  const [, setCaptchaVerified] = useState(false);

  const { data: feedbackTypes = [] } = useOptions({ gkey: 'feedbackcat' });
  const [hasSubmitted, setHasSubmitted] = useState(() => {
    if (typeof window !== 'undefined') {
      return sessionStorage?.getItem('hasSubmittedFeedback') === 'true';
    }
    return false;
  });

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const feedback = watch('feedback');
  const catId = watch('catId');
  const { ref: feedbackRegisterRef, ...feedbackRegister } = register(
    'feedback',
    { required: true }
  );

  useEffect(() => {
    if (user?.phone) {
      setValue('phone', String(user.phone));
    }
  }, [user?.phone, setValue]);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [feedback]);

  const handleFileUpload = (file: File) => {
    setFileInfo((prev) => [
      ...prev,
      {
        name: file.name,
        size: file.size,
        isLoading: false,
        file,
      },
    ]);
  };

  const handleRemoveFile = (index: number) => {
    setFileInfo((prev) => prev.filter((_, i) => i !== index));
  };

  const handleClose = () => {
    setFileInfo([]);
    clearErrors();
    reset();
    setCaptchaInput('');
    onClose();
  };

  const handleCaptchaVerify = (isVerified: boolean) => {
    setCaptchaVerified(isVerified);
  };

  const handleCaptchaInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCaptchaInput(e.target.value);
    clearErrors('captcha');
  };

  const onSubmit = async (data: FeedbackFormValues) => {
    const isValid = captchaRef.current?.verifyCaptcha(captchaInput);
    if (hasSubmitted && !isValid) {
      toast.error('Код буруу оруулсан байна. Дахин оролдоно уу.');
      return;
    }

    if (!data.catId) {
      toast.error('Санал сэтгэгдлийн төрөл сонгоно уу.');
      return;
    }

    try {
      await createFeedback({
        phone: Number(user?.phone ?? data.phone),
        catId: Number(data.catId),
        feedback: data.feedback,
        files: fileInfo.map((item) => item.file),
        projectId: 1,
        companyId: 0,
      });
      onSuccess();
      handleClose();
      setHasSubmitted(true);
      sessionStorage?.setItem('hasSubmittedFeedback', 'true');
    } catch {
      toast.error('Санал хүсэлт илгээх үед алдаа гарлаа');
    }
  };

  return (
    <Dialog
      closeOnClickOutside={false}
      isOpen={isOpen}
      onClose={handleClose}
      title={
        <p className="font-bold text-[#000000] text-[20px]">Санал хүсэлт</p>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-normal text-[#000000A3] mb-1">
              Утасны дугаар
            </label>
            {user?.phone ? (
              <>
                <input type="hidden" {...register('phone')} />
                <input
                  value={user.phone}
                  type="number"
                  readOnly
                  placeholder="Заавал биш"
                  className="w-full p-3 border border-gray-300 rounded-lg text-sm disabled:text-[#28282D]"
                />
              </>
            ) : (
              <input
                type="number"
                {...register('phone', {
                  required: !user?.phone,
                  pattern: {
                    value: /^\d{8}$/,
                    message: 'Утасны дугаар буруу байна',
                  },
                })}
                onChange={() => clearErrors('phone')}
                placeholder="Утасны дугаар"
                className={getInputClassName(!!errors.phone)}
              />
            )}
            {!!errors.phone && (
              <p className="text-red-500 text-xs mt-1">
                {errors.phone.message || 'Утасны дугаар оруулна уу'}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-normal text-[#000000A3] mb-1">
              Санал сэтгэгдлийн төрөл
            </label>
            <select
              {...register('catId', {
                required: true,
                valueAsNumber: true,
              })}
              onChange={(e) => {
                const value = e.target.value;
                setValue('catId', value === '' ? '' : Number(value));
                clearErrors('catId');
              }}
              className={`w-full p-3 rounded-lg text-sm appearance-none pr-10 
                            bg-[url('/icons/icon-chevron-down.svg')] bg-no-repeat bg-[center_right_1rem] 
                            ${catId === '' ? 'text-[#000000A3]' : 'text-black'} 
                            ${getInputClassName(!!errors.catId)}
                        `}
            >
              <option value="" hidden>
                Сонгоно уу
              </option>
              {feedbackTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name_mn || type.name_en}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-normal text-[#000000A3] mb-1">
              Санал хүсэлт
            </label>
            <textarea
              {...feedbackRegister}
              ref={(element) => {
                feedbackRegisterRef(element);
                textareaRef.current = element;
              }}
              onChange={(e) => {
                setValue('feedback', e.target.value);
                clearErrors('feedback');
              }}
              placeholder="Оруулна уу"
              style={{ resize: 'none' }}
              maxLength={400}
              className={`${getInputClassName(!!errors.feedback)} 
                        w-full p-3 rounded-lg text-sm font-normal text-black
                        focus:outline-none focus:ring-1 input-field resize-y scrollbar-thin 
                        scrollbar-thumb-gray-400 scrollbar-track-gray-100
                    `}
            />
          </div>

          <div>
            <label className="block text-sm font-normal text-[#000000A3] mb-1">
              Файл
            </label>
            <FileUploader
              fileInfo={fileInfo}
              onAddFile={handleFileUpload}
              onRemove={handleRemoveFile}
            />
          </div>

          {hasSubmitted && (
            <>
              <div className="mt-4 ">
                <Captcha
                  ref={captchaRef}
                  onVerify={handleCaptchaVerify}
                  className="mb-3"
                />
              </div>
              <div className="flex flex-col w-full gap-y-1">
                <InputString
                  register={{
                    ...register('captcha', {
                      required: hasSubmitted,
                    }),
                  }}
                  name="captcha"
                  label="Дараах кодыг оруулна уу"
                  className={`${getInputClassName(!!errors.captcha)} w-full border border-[#9D9D9D] rounded-lg h-10 p-3 text-xs text-zinc-900 focus:border-primary`}
                  id="captcha"
                  required={true}
                  placeholder="Заавал оруулах"
                  value={captchaInput}
                  onChange={handleCaptchaInputChange}
                />
              </div>
            </>
          )}
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="submit"
            className="w-[207px] py-[9px] text-sm disabled:bg-[#D9D9D9] disabled:text-[#28282D] font-medium text-white bg-primary rounded-[8px] hover:bg-[#003C77] focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            {isSubmitting ? 'Илгээж байна...' : 'Илгээх'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
